/* URBRAND standard pages — v5.9.7 */
(() => {
  'use strict';

  const instances = new WeakMap();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const slugify = (value) => {
    const normalized = String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
    return normalized || 'secao';
  };

  const uniqueId = (base, used) => {
    let candidate = base;
    let index = 2;
    while (used.has(candidate) || document.getElementById(candidate)) {
      candidate = `${base}-${index}`;
      index += 1;
    }
    used.add(candidate);
    return candidate;
  };

  const setStatus = (root, message) => {
    const status = root.querySelector('[data-page-status]');
    if (!status) return;
    status.textContent = message;
    window.clearTimeout(status._clearTimer);
    status._clearTimer = window.setTimeout(() => {
      status.textContent = '';
    }, 2600);
  };

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    document.execCommand('copy');
    field.remove();
  };

  const enhanceRichContent = (root, cleanup) => {
    root.querySelectorAll('[data-page-content] table').forEach((table) => {
      if (table.parentElement?.classList.contains('page-standard__table-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'page-standard__table-scroll';
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('tabindex', '0');
      wrapper.setAttribute('aria-label', root.dataset.tableLabel || 'Tabela com rolagem horizontal');
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });

    root.querySelectorAll('[data-page-content] img').forEach((image) => {
      if (!image.hasAttribute('loading')) image.loading = 'lazy';
      if (!image.hasAttribute('decoding')) image.decoding = 'async';
    });

    root.querySelectorAll('[data-page-content] iframe').forEach((frame) => {
      if (!frame.hasAttribute('loading')) frame.loading = 'lazy';
      if (!frame.hasAttribute('title')) frame.title = document.title;
    });

    if (root.dataset.externalLinks === 'true') {
      root.querySelectorAll('[data-page-content] a[href]').forEach((link) => {
        let url;
        try {
          url = new URL(link.href, window.location.href);
        } catch {
          return;
        }
        if (!['http:', 'https:'].includes(url.protocol) || url.origin === window.location.origin) return;
        link.target = '_blank';
        const rel = new Set((link.rel || '').split(/\s+/).filter(Boolean));
        rel.add('noopener');
        rel.add('noreferrer');
        link.rel = [...rel].join(' ');
      });
    }

    cleanup.push(() => {
      root.querySelectorAll('[data-page-status]').forEach((status) => window.clearTimeout(status._clearTimer));
    });
  };

  const initHeadings = (root, cleanup) => {
    const content = root.querySelector('[data-page-content]');
    const toc = root.querySelector('[data-page-toc]');
    const tocList = root.querySelector('[data-page-toc-list]');
    if (!content) return [];

    const selectors = root.dataset.tocLevels || 'h2,h3';
    const headings = [...content.querySelectorAll(selectors)]
      .filter((heading) => heading.textContent.trim() && !heading.closest('[hidden]'));
    const usedIds = new Set();

    headings.forEach((heading) => {
      heading.id = heading.id || uniqueId(slugify(heading.textContent), usedIds);
      usedIds.add(heading.id);

      if (root.dataset.anchorLinks === 'true' && !heading.querySelector('.page-standard__heading-anchor')) {
        const anchor = document.createElement('button');
        anchor.type = 'button';
        anchor.className = 'page-standard__heading-anchor';
        anchor.setAttribute('aria-label', `Copiar link para ${heading.textContent.trim()}`);
        anchor.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.1 1.1"/><path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.1-1.1"/></svg>';
        const onCopy = async () => {
          const url = new URL(window.location.href);
          url.hash = heading.id;
          try {
            await copyText(url.href);
            setStatus(root, root.dataset.linkCopied || 'Link copiado.');
          } catch {
            setStatus(root, root.dataset.copyError || 'Não foi possível copiar o link.');
          }
        };
        anchor.addEventListener('click', onCopy);
        heading.appendChild(anchor);
        cleanup.push(() => anchor.removeEventListener('click', onCopy));
      }
    });

    if (!toc || !tocList || headings.length < 2) {
      toc?.setAttribute('hidden', '');
      const sidebar = root.querySelector('[data-page-sidebar]');
      if (sidebar?.dataset.hasStaticSidebar !== 'true') sidebar?.setAttribute('hidden', '');
      root.classList.remove('page-standard--toc-ready');
      return headings;
    }

    const fragment = document.createDocumentFragment();
    headings.forEach((heading) => {
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.dataset.level = heading.tagName.slice(1);
      link.textContent = heading.childNodes[0]?.textContent?.trim() || heading.textContent.trim();
      fragment.appendChild(link);
    });
    tocList.replaceChildren(fragment);
    toc.removeAttribute('hidden');
    root.querySelector('[data-page-sidebar]')?.removeAttribute('hidden');
    root.classList.add('page-standard--toc-ready');
    cleanup.push(() => root.classList.remove('page-standard--toc-ready'));

    const links = [...tocList.querySelectorAll('a')];
    const activate = (id) => {
      links.forEach((link) => {
        const active = link.getAttribute('href') === `#${id}`;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    const onTocClick = (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(null, '', link.hash);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      window.setTimeout(() => target.removeAttribute('tabindex'), 500);
    };
    tocList.addEventListener('click', onTocClick);
    cleanup.push(() => tocList.removeEventListener('click', onTocClick));

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) activate(visible[0].target.id);
      }, { rootMargin: '-18% 0px -70%', threshold: [0, 1] });
      headings.forEach((heading) => observer.observe(heading));
      cleanup.push(() => observer.disconnect());
    }

    return headings;
  };

  const initReadingTime = (root) => {
    const output = root.querySelector('[data-page-reading-time]');
    const minutesNode = root.querySelector('[data-page-reading-minutes]');
    const content = root.querySelector('[data-page-content]');
    if (!output || !minutesNode || !content) return;
    const words = (content.textContent.trim().match(/\S+/g) || []).length;
    if (!words) return;
    const speed = Math.max(100, Number(root.dataset.readingSpeed) || 220);
    minutesNode.textContent = String(Math.max(1, Math.ceil(words / speed)));
    output.hidden = false;
  };

  const initProgress = (root, cleanup) => {
    const progress = root.querySelector('[data-page-progress]');
    const bar = root.querySelector('[data-page-progress-bar]');
    const article = root.querySelector('[data-page-article]');
    if (!progress || !bar || !article) return;

    let ticking = false;
    const update = () => {
      ticking = false;
      const rect = article.getBoundingClientRect();
      const start = window.scrollY + rect.top - window.innerHeight * 0.25;
      const end = start + article.offsetHeight - window.innerHeight * 0.5;
      const range = Math.max(1, end - start);
      const ratio = Math.min(1, Math.max(0, (window.scrollY - start) / range));
      bar.style.transform = `scaleX(${ratio})`;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
    cleanup.push(() => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    });
  };

  const initUtilities = (root, cleanup) => {
    const share = root.querySelector('[data-page-share]');
    const print = root.querySelector('[data-page-print]');
    const back = root.querySelector('[data-page-back-top]');

    const onShare = async () => {
      const title = share?.dataset.shareTitle || document.title;
      const url = share?.dataset.shareUrl || window.location.href;
      try {
        if (navigator.share) {
          await navigator.share({ title, url });
          setStatus(root, root.dataset.shareSuccess || 'Página compartilhada.');
        } else {
          await copyText(url);
          setStatus(root, root.dataset.linkCopied || 'Link copiado.');
        }
      } catch (error) {
        if (error?.name !== 'AbortError') setStatus(root, root.dataset.shareError || 'Não foi possível compartilhar.');
      }
    };
    const onPrint = () => window.print();
    const onBack = () => {
      root.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      root.focus?.({ preventScroll: true });
    };

    share?.addEventListener('click', onShare);
    print?.addEventListener('click', onPrint);
    back?.addEventListener('click', onBack);
    cleanup.push(() => {
      share?.removeEventListener('click', onShare);
      print?.removeEventListener('click', onPrint);
      back?.removeEventListener('click', onBack);
    });
  };

  const init = (root) => {
    if (!(root instanceof HTMLElement) || instances.has(root)) return;
    const cleanup = [];
    enhanceRichContent(root, cleanup);
    initHeadings(root, cleanup);
    initReadingTime(root);
    initProgress(root, cleanup);
    initUtilities(root, cleanup);
    instances.set(root, () => cleanup.splice(0).forEach((fn) => fn()));
  };

  const destroy = (root) => {
    const teardown = instances.get(root);
    if (teardown) teardown();
    instances.delete(root);
  };

  const boot = (scope = document) => {
    if (scope.matches?.('[data-standard-page]')) init(scope);
    scope.querySelectorAll?.('[data-standard-page]').forEach(init);
  };

  boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-standard-page]')) destroy(event.target);
    event.target.querySelectorAll?.('[data-standard-page]').forEach(destroy);
  });
})();
